import { useMemo } from "react";
import {
  type UseMutationResult,
  type UseMutationOptions,
  useMutation,
  //
  type UseQueryOptions,
  type UseQueryResult,
  useQuery,
} from "@tanstack/react-query";
import { backendApi } from "src/backendApi/backendApi";
import type {
  ApiV3PasswordRequestResetResponse,
  ApiV3PasswordResetCompleteBody,
  ApiV3PasswordResetCompleteResponse,
  ApiV3PasswordResetValidateResponse,
} from "@typings/mightierApiTypes";

/**
 * REQUEST PASSWORD RESET - STEP1
 */

type RequestPasswordResetMutationResult = UseMutationResult<
  ApiV3PasswordRequestResetResponse,
  unknown,
  ApiV3PasswordRequestResetResponse
>;

type RequestPasswordResetMutationOptions = UseMutationOptions<
  ApiV3PasswordRequestResetResponse,
  unknown,
  ApiV3PasswordRequestResetResponse
>;

export const useRequestPasswordResetMutation = (
  options: RequestPasswordResetMutationOptions,
): RequestPasswordResetMutationResult => {
  return useMutation({
    ...options,
    mutationFn: async ({ email }) => {
      const resp = await backendApi.initiatePasswordReset(email);
      return resp.data;
    },
    mutationKey: ["password-reset"],
  });
};

/**
 * VALIDATE THAT UID/TOKEN ARE CORRECT
 *
 * TODO STUBBBB
 */

type ValidatePasswordResetTokenQueryResult =
  UseQueryResult<ApiV3PasswordResetValidateResponse>;

type ValidatePasswordResetTokenQueryOptions =
  UseQueryOptions<ApiV3PasswordResetValidateResponse>;

export const makePasswordResetQueryOpts = (
  token: string | undefined,
  uidb64: string,
  enabled: ValidatePasswordResetTokenQueryOptions["enabled"],
): ValidatePasswordResetTokenQueryOptions => {
  const ret: ValidatePasswordResetTokenQueryOptions = {
    queryFn: async () => {
      if (!token) {
        throw new Error("Token is required");
      }
      // NOTE: while it is weird to have a query for a POST request,
      // we do so because we want to use the query cache to store the result.
      const data = { token, uidb64 };
      const resp = await backendApi.validatePasswordResetToken(data);
      return resp.data;
    },
    queryKey: ["password-reset-validate", { token, uidb64 }],
    // make sure it only fires one time (except for refresh)
    staleTime: Infinity,
    gcTime: Infinity,
    // don't retry - if it fails it's like due to 400 so no point in retrying
    retry: false,
    //
    enabled,
  };
  return ret;
};

export const useValidatePasswordResetTokenQuery = (
  token: string | undefined,
  uidb64: string,
  options?: ValidatePasswordResetTokenQueryOptions,
): ValidatePasswordResetTokenQueryResult => {
  const opts = useMemo(
    () => makePasswordResetQueryOpts(token, uidb64, options?.enabled),
    [token, uidb64, options?.enabled],
  );

  return useQuery(opts);
};

/**
 * COMPLETE PASSWORD RESET - STEP2
 *
 * actually set the password
 */

type CompletePasswordResetMutationResult = UseMutationResult<
  ApiV3PasswordResetCompleteResponse,
  unknown,
  ApiV3PasswordResetCompleteBody
>;

type CompletePasswordResetMutationOptions = UseMutationOptions<
  ApiV3PasswordResetCompleteResponse,
  unknown,
  ApiV3PasswordResetCompleteBody
>;

export const useCompletePasswordResetMutation = (
  options: CompletePasswordResetMutationOptions,
): CompletePasswordResetMutationResult => {
  return useMutation({
    ...options,
    mutationFn: async (data) => {
      const resp = await backendApi.passwordResetSetPassword(data);
      return resp.data;
    },
    mutationKey: ["password-reset-complete"],
  });
};
